import { useEffect, useState } from "react";
import { CheckCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import { Button, Space, Table } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/lib/table";
import type { SorterResult } from "antd/lib/table/interface";
import moment from "moment";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useEmailDocumentsPageQuery } from "../../../api/graphql/queries/emailDocumentsPage/emailDocumentsPage.generated";
import type { EmailDocumentsPageQuery } from "../../../api/graphql/queries/emailDocumentsPage/emailDocumentsPage.generated";
import {
  DocumentUploadSource,
  EmailDocumentSortField,
  MatchingStatusFilter,
  SortOrder,
} from "../../../api/graphql/schema.generated";
import { DISPLAY_DATE_FORMAT } from "../../../constants";
import { formatDEAmount } from "../../../utils/amount";
import BulkDeleteEmailDocumentModal from "./BulkDeleteEmailDocumentModal";
import { ReceiptDetailPanel } from "./ReceiptDetailPanel";
import { SourceBadge } from "./SourceBadge";
import UploadReceiptButton from "./UploadReceiptButton";

const DEFAULT_PAGE_SIZE = 50;

// SMT-1127: keep the column header visible while scrolling (like
// VerificationView). CSS `position: sticky` on the header cells keeps the
// table DOM intact (unlike antd's `sticky` prop, which splits header/body).
// `top` sits just below the sticky toolbar above the table.
const StickyTableWrapper = styled.div`
  .ant-table-thead > tr > th {
    position: sticky;
    top: 79px;
    z-index: 4;
    /* Opaque background so scrolling rows don't bleed through the sticky
       header (the sorted column otherwise shows through). */
    background: #fafafa;
  }
`;

export type ReceiptRow =
  EmailDocumentsPageQuery["emailDocumentsPage"]["items"][number];

/** Subset of EmailDocumentsListOptions that the FilterPanel controls. */
export type ReceiptFilterValues = {
  dateFrom?: string;
  dateTo?: string;
  uploadDateFrom?: string;
  uploadDateTo?: string;
  uploadSources?: DocumentUploadSource[];
  /*
   * Exact origins (SMT-1191). Kept as its own list rather than folded into
   * uploadSources because the backend ORs the two, which is what lets one filter
   * option — Web, Mobile — span both families at once.
   */
  documentOrigins?: string[];
  matchingStatus?: MatchingStatusFilter;
};

const toSortOrder = (order: SorterResult<ReceiptRow>["order"]): SortOrder =>
  order === "ascend" ? SortOrder.ASC : SortOrder.DESC;

const formatDate = (value?: string | null) =>
  value ? moment(value).format(DISPLAY_DATE_FORMAT) : "";

const formatAmount = (amount?: number | null, currency?: string | null) =>
  amount == null ? "" : formatDEAmount(amount, currency ?? undefined) ?? "";

export const ReceiptTable = ({
  publicId,
  email,
  filters,
  onReceiptUploaded,
  onReceiptsDeleted,
}: {
  publicId: string;
  email?: string;
  filters?: ReceiptFilterValues;
  /** SMT-997: called after a successful upload so the parent can switch the
   * date filter to "Erstellt am" (a fresh receipt has no invoice date yet and
   * would otherwise be hidden by the default Rechnungsdatum filter). */
  onReceiptUploaded?: () => void;
  /** SMT-1238 reviewer finding: called after a successful delete, on both
   * delete paths this table owns — the detail panel's single delete and the
   * bulk-delete modal — so the parent can refetch the source facets. Without
   * it, a source whose last receipt was just deleted would keep showing in the
   * dropdown until the next reload. */
  onReceiptsDeleted?: () => void;
}) => {
  const { t } = useTranslation("ReceiptOverview");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  // SMT-997: multi-select for bulk delete. Selection is scoped to the current
  // customer (the parent remounts this subtree on customer change via `key`)
  // and is cleared when the filters change (see the reset effect below).
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [sortField, setSortField] = useState<EmailDocumentSortField>(
    EmailDocumentSortField.UPLOAD_DATE
  );
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DESC);

  // Reset to page 1 whenever the active filters change so stale offsets don't
  // produce an empty result when a narrow filter reduces the total count.
  // Depend on primitive fields — the parent rebuilds `filters` as a fresh
  // literal each render, so depending on the object reference would refire on
  // every parent re-render and silently snap pagination back to 1. The
  // upload-sources array is joined into a stable string for the same reason.
  const uploadSourcesKey = (filters?.uploadSources ?? []).join("|");
  const documentOriginsKey = (filters?.documentOrigins ?? []).join("|");
  useEffect(() => {
    setPage(1);
    // Drop the selection when the filter set changes so we never bulk-delete
    // rows the operator can no longer see.
    setSelectedRowKeys([]);
  }, [
    filters?.dateFrom,
    filters?.dateTo,
    filters?.uploadDateFrom,
    filters?.uploadDateTo,
    uploadSourcesKey,
    documentOriginsKey,
    filters?.matchingStatus,
  ]);

  const { data, loading, refetch } = useEmailDocumentsPageQuery({
    variables: {
      publicId,
      options: {
        limit: pageSize,
        offset: (page - 1) * pageSize,
        sortField,
        sortOrder,
        ...(filters?.dateFrom ? { dateFrom: filters.dateFrom } : {}),
        ...(filters?.dateTo ? { dateTo: filters.dateTo } : {}),
        ...(filters?.uploadDateFrom
          ? { uploadDateFrom: filters.uploadDateFrom }
          : {}),
        ...(filters?.uploadDateTo
          ? { uploadDateTo: filters.uploadDateTo }
          : {}),
        ...(filters?.uploadSources?.length
          ? { uploadSources: filters.uploadSources }
          : {}),
        ...(filters?.documentOrigins?.length
          ? { documentOrigins: filters.documentOrigins }
          : {}),
        ...(filters?.matchingStatus
          ? { matchingStatus: filters.matchingStatus }
          : {}),
      },
    },
  });

  const items = data?.emailDocumentsPage.items ?? [];
  const total = data?.emailDocumentsPage.total ?? 0;

  // Determine whether any filter is currently active so we can show a more
  // informative empty state when filters narrow the result to zero rows.
  const hasActiveFilters = Boolean(
    filters?.dateFrom ||
      filters?.dateTo ||
      filters?.uploadDateFrom ||
      filters?.uploadDateTo ||
      filters?.uploadSources?.length ||
      filters?.documentOrigins?.length ||
      filters?.matchingStatus
  );

  const columns: ColumnsType<ReceiptRow> = [
    {
      title: t("columns.name"),
      key: EmailDocumentSortField.NAME,
      sorter: true,
      // Corrected values (correctionData) override the raw OCR fields so the
      // overview reflects edits made in the detail panel.
      render: (_, row) => row.correctionData?.name ?? row.name,
    },
    {
      title: t("columns.amount"),
      key: EmailDocumentSortField.AMOUNT,
      sorter: true,
      align: "right",
      render: (_, row) =>
        formatAmount(
          row.correctionData?.amount ?? row.amount,
          row.correctionData?.currency ?? row.currency
        ),
    },
    {
      title: t("columns.documentNumber"),
      key: EmailDocumentSortField.DOCUMENT_NUMBER,
      sorter: true,
      render: (_, row) =>
        row.correctionData?.documentNumber ?? row.documentNumber,
    },
    {
      title: t("columns.invoiceDate"),
      key: EmailDocumentSortField.INVOICE_DATE,
      sorter: true,
      align: "center",
      render: (_, row) => formatDate(row.correctionData?.date ?? row.date),
    },
    {
      title: t("columns.uploadDate"),
      key: EmailDocumentSortField.UPLOAD_DATE,
      sorter: true,
      defaultSortOrder: "descend",
      align: "center",
      render: (_, row) => formatDate(row.createdAt),
    },
    {
      title: t("columns.source"),
      key: "source",
      align: "center",
      render: (_, row) => (
        <SourceBadge
          source={row.uploadSource}
          documentOrigin={row.documentOrigin}
        />
      ),
    },
    {
      // A receipt is "matched" when it has a linked transaction. Matched rows
      // link out to that transaction; unmatched rows (no transactionId) show a
      // dash. Non-sortable — there is no MATCH_STATUS sort field on the backend.
      title: t("columns.matchedToTransaction"),
      key: "matchedToTransaction",
      align: "center",
      render: (_, row) =>
        row.transactionId ? (
          <a
            data-test="receiptMatchedLink"
            href={`/transaction-view?id=${row.transactionId}`}
            target="_blank"
            rel="noreferrer"
            aria-label={t("columns.matchedToTransaction") ?? undefined}
          >
            <CheckCircleOutlined style={{ color: "#52c41a" }} />
          </a>
        ) : (
          <span data-test="receiptUnmatched">—</span>
        ),
    },
  ];

  const handleChange = (
    pagination: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<ReceiptRow> | SorterResult<ReceiptRow>[]
  ) => {
    const nextPage = pagination.current ?? 1;
    const nextPageSize = pagination.pageSize ?? DEFAULT_PAGE_SIZE;

    // Resolve the next sort (falling back to the default when a third header
    // click clears it).
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextSortField =
      activeSorter?.columnKey && activeSorter.order
        ? (activeSorter.columnKey as EmailDocumentSortField)
        : EmailDocumentSortField.UPLOAD_DATE;
    const nextSortOrder =
      activeSorter?.columnKey && activeSorter.order
        ? toSortOrder(activeSorter.order)
        : SortOrder.DESC;

    // Clear the selection whenever the query that defines the visible rows
    // changes — page, pageSize, or sort. Selection is not carried across pages,
    // and a server-side re-sort re-slices the pages, so keeping it would let
    // "Delete selected" remove rows the operator can no longer see.
    if (
      nextPage !== page ||
      nextPageSize !== pageSize ||
      nextSortField !== sortField ||
      nextSortOrder !== sortOrder
    ) {
      setSelectedRowKeys([]);
    }

    setPage(nextPage);
    setPageSize(nextPageSize);
    setSortField(nextSortField);
    setSortOrder(nextSortOrder);
  };

  // SMT-997: open/close the detail panel by clicking anywhere on the row (the
  // +/− expand column is dropped). Only one row is expanded at a time.
  const toggleExpanded = (id: string) =>
    setExpandedRowKeys((prev) => (prev.includes(id) ? [] : [id]));

  return (
    <StickyTableWrapper>
      {/* Toolbar: bulk delete on the left, "Upload Receipt" on the right
          (SMT-997). SMT-1127: the delete button is always visible and disabled
          until a row is selected; the toolbar is sticky so the actions stay
          reachable while scrolling, like VerificationView. */}
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          position: "sticky",
          top: 17,
          zIndex: 5,
          background: "#fff",
          padding: "15px 0",
        }}
      >
        <span>
          <Button
            danger
            icon={<DeleteOutlined />}
            disabled={selectedRowKeys.length === 0}
            onClick={() => setBulkDeleteOpen(true)}
            data-test="bulkDeleteButton"
          >
            {t("actions.deleteSelected", { count: selectedRowKeys.length })}
          </Button>
        </span>
        <UploadReceiptButton
          email={email}
          onUploaded={() => {
            // Switch the parent's date filter to "Erstellt am" so the freshly
            // uploaded receipt (no invoice date yet) is visible, then refetch.
            onReceiptUploaded?.();
            refetch();
          }}
        />
      </Space>
      <Table<ReceiptRow>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={items}
        pagination={{
          current: page,
          pageSize,
          total,
          // Pagination controls both above and below the table (like
          // VerificationView) so operators can page without scrolling.
          position: ["topRight", "bottomRight"],
          /*
           * SMT-1318: the default page size of 50 gave operators no way to
           * tell how many receipts a filter actually returns, so they
           * estimated the number for customers. `total` is the same count the
           * pager already uses, so the label can never disagree with the pages.
           */
          showTotal: (totalCount) =>
            t("pagination.total", { count: totalCount }),
        }}
        onChange={handleChange}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as string[]),
        }}
        onRow={(record) => ({
          style: { cursor: "pointer" },
          onClick: (e) => {
            // Don't toggle the detail panel when the click targets the row's
            // selection checkbox, an action button, or a link.
            if (
              (e.target as HTMLElement).closest(
                ".ant-table-selection-column, button, a"
              )
            ) {
              return;
            }
            toggleExpanded(record.id);
          },
        })}
        locale={{
          emptyText: hasActiveFilters
            ? t("filter.noResults")
            : t("filter.noReceipts"),
        }}
        expandable={{
          // Row click drives expansion now — hide the dedicated +/− column.
          showExpandColumn: false,
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
          expandedRowRender: (record) => (
            <ReceiptDetailPanel
              emailDocument={record}
              publicId={publicId}
              email={email}
              // The client runs `no-cache`, so a saved correction (or a match)
              // can't propagate through the normalized cache — refetch the current
              // page (keeping the active sort/pagination) so the edited row
              // reflects the change.
              onSaved={() => refetch()}
              onDeleted={() => {
                setExpandedRowKeys((prev) =>
                  prev.filter((k) => k !== record.id)
                );
                setSelectedRowKeys((prev) =>
                  prev.filter((k) => k !== record.id)
                );
                refetch();
                // SMT-1238 reviewer finding: this receipt may have been the
                // last one from its source — let the parent refetch the
                // dropdown's facets so a now-empty source disappears.
                onReceiptsDeleted?.();
              }}
            />
          ),
        }}
      />
      <BulkDeleteEmailDocumentModal
        isOpen={bulkDeleteOpen}
        emailDocumentIds={selectedRowKeys}
        onClose={() => setBulkDeleteOpen(false)}
        onSuccess={() => {
          setBulkDeleteOpen(false);
          // The bulk mutation returns a count, not per-id results, so clear the
          // whole selection and collapse any selected expanded row; the refetch
          // reflects what actually remains.
          setExpandedRowKeys((prev) =>
            prev.filter((k) => !selectedRowKeys.includes(k))
          );
          setSelectedRowKeys([]);
          refetch();
          // SMT-1238 reviewer finding: same as the single-delete path above —
          // a bulk delete can just as easily empty out a source.
          onReceiptsDeleted?.();
        }}
      />
    </StickyTableWrapper>
  );
};
